import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  Factory,
  Globe,
  LogOut,
  Lock,
  MapPin,
  Phone,
  RefreshCw,
  Shield,
  Users,
  X,
} from "lucide-react";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useRBAC } from "../hooks/useRBAC";
import { useOrgHierarchySync } from "../hooks/useOrgHierarchySync";
import { usePollOrganizations } from "../hooks/usePollOrganizations";
import LoadingSpinner from "../components/LoadingSpinner";
import { Button } from "../components/Button";
import NewOrganizationWizard from "../components/NewOrganizationWizard";

// ── IP / CIDR matching (mirrors backend logic in JS) ─────────────────────────
const ipToInt = (ip) =>
  ip.split('.').reduce((acc, oct) => ((acc << 8) + parseInt(oct, 10)) >>> 0, 0);

const isIpAllowed = (clientIp, entry) => {
  if (!entry || !clientIp) return false;
  const trimmed = entry.trim();
  if (!trimmed.includes('/')) return clientIp === trimmed;
  const [range, bits] = trimmed.split('/');
  const mask = bits === '0' ? 0 : (~0 << (32 - parseInt(bits, 10))) >>> 0;
  return (ipToInt(clientIp) & mask) === (ipToInt(range) & mask);
};

const isOrgIpBlocked = (org, clientIp) => {
  if (!org.allowedIpRanges) return false;          // no restriction
  if (!clientIp) return false;                      // IP not yet known — don't block
  const ranges = org.allowedIpRanges.split(',').map(s => s.trim()).filter(Boolean);
  if (ranges.length === 0) return false;
  return !ranges.some(r => isIpAllowed(clientIp, r));
};
// ─────────────────────────────────────────────────────────────────────────────

// ── Tile renderers ──────────────────────────────────────────────────────────
const CreatingTile = ({ org }) => (
  <div
    className="relative bg-theme-card-solid border border-accent-cyan/25 rounded-2xl p-5 cursor-default transition duration-300"
    aria-disabled="true"
  >
    <div className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold bg-accent-cyan/10 text-accent-cyan">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-accent-cyan" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-cyan" />
      </span>
      Creating
    </div>
    <h3 className="text-xl font-semibold mb-3 text-content-secondary pr-24">
      {org.organizationName}
    </h3>
    <p className="text-xs leading-relaxed text-content-secondary mb-3">
      Provisioning schemas across this installation&apos;s regions. This usually takes 1–2 minutes.
    </p>
    <div className="h-1 rounded-full overflow-hidden bg-theme-tertiary">
      <div
        className="h-full rounded-full relative overflow-hidden bg-gradient-primary"
        style={{ width: '45%' }}
      >
        <div className="shimmer" />
      </div>
    </div>
  </div>
);

const FailedTile = ({ org, onRetry, retrying }) => (
  <div className="relative bg-theme-card-solid border border-risk-critical/30 rounded-2xl p-5 transition duration-300">
    <div className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold bg-risk-critical/10 text-risk-critical">
      <AlertTriangle className="w-3 h-3" />
      Failed
    </div>
    <h3 className="text-xl font-semibold mb-3 text-content-secondary pr-20">
      {org.organizationName}
    </h3>
    <p className="text-xs leading-relaxed text-content-secondary mb-3">
      {org.statusError || 'Provisioning failed. Retry to try again, or contact support if this keeps happening.'}
    </p>
    <div className="flex items-center gap-2">
      <Button variant="danger" size="sm" onClick={() => onRetry(org)} disabled={retrying}>
        {retrying ? 'Retrying…' : 'Retry'}
      </Button>
    </div>
  </div>
);

const SuccessToast = ({ toast, onDismiss, onOpen }) => (
  <div
    className="fixed bottom-8 right-8 flex items-start gap-3 p-4 rounded-xl shadow-theme-lg bg-theme-secondary border border-border z-50"
    style={{ width: 360 }}
    role="status"
  >
    <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center bg-status-success/15">
      <Check className="w-4 h-4 text-status-success" strokeWidth={3} />
    </div>
    <div className="flex-1">
      <p className="text-sm font-semibold text-content-primary">
        {toast.org.organizationName} is ready
      </p>
      <p className="text-xs mt-0.5 leading-relaxed text-content-secondary">
        This installation&apos;s regions are provisioned. You can start inviting users.
      </p>
      <div className="flex items-center gap-3 mt-2">
        <button
          type="button"
          className="text-xs font-semibold text-accent-cyan hover:underline"
          onClick={() => onOpen(toast.org)}
        >
          Open organization →
        </button>
        <button
          type="button"
          className="text-xs font-medium text-content-muted hover:text-content-secondary"
          onClick={onDismiss}
        >
          Dismiss
        </button>
      </div>
    </div>
    <button
      type="button"
      onClick={onDismiss}
      className="flex-shrink-0 text-content-muted hover:text-content-secondary"
      aria-label="Dismiss"
    >
      <X className="w-4 h-4" />
    </button>
  </div>
);

// ── Main component ──────────────────────────────────────────────────────────
const TenantSelection = () => {
  const [organizations, setOrganizations] = useState([]);
  const [regions, setRegions] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [detectedIp, setDetectedIp] = useState(null);
  const [ipBlockedMsg, setIpBlockedMsg] = useState(() => {
    const msg = sessionStorage.getItem("ipBlockedMessage");
    const ip  = sessionStorage.getItem("ipBlockedDetectedIp");
    if (msg) {
      sessionStorage.removeItem("ipBlockedMessage");
      sessionStorage.removeItem("ipBlockedDetectedIp");
      return { message: msg, detectedIp: ip };
    }
    return null;
  });

  // Phase-2 UX state
  const [toast, setToast] = useState(null);          // { org } on success transition
  const [ringOrgId, setRingOrgId] = useState(null);  // shows green halo for ~6s
  const [retryingId, setRetryingId] = useState(null);

  const { user } = useAuth();
  const { hasMinimumRoleAtOrAbove, isAssignmentScopedOnly, userRoles, SCOPES } = useRBAC();
  const { refreshOrgHierarchy } = useOrgHierarchySync();

  // ── RBAC ──────────────────────────────────────────────────────────────────
  const ownerTenants = useMemo(() => {
    try {
      const orgHierarchy = JSON.parse(localStorage.getItem("orgHierarchy") || "[]");
      const userRolesData = JSON.parse(localStorage.getItem("userRoles") || "[]");
      const tenantMap = new Map();

      orgHierarchy.forEach(h => {
        if (h.tenantId && !tenantMap.has(h.tenantId)) {
          tenantMap.set(h.tenantId, {
            tenantId: h.tenantId,
            tenantName: h.tenantName || `Tenant ${h.tenantId}`,
          });
        }
      });

      userRolesData.forEach(r => {
        if (r.scope === 'TENANT' && r.scopeEntityId && !tenantMap.has(r.scopeEntityId)) {
          tenantMap.set(r.scopeEntityId, {
            tenantId: r.scopeEntityId,
            tenantName: `Tenant ${r.scopeEntityId}`,
          });
        }
      });

      return [...tenantMap.values()].filter(t =>
        hasMinimumRoleAtOrAbove('ADMIN', 'TENANT', t.tenantId)
      );
    } catch (e) {
      return [];
    }
  }, [hasMinimumRoleAtOrAbove]);

  const canAddOrganization = ownerTenants.length > 0;

  // ── Data fetching ─────────────────────────────────────────────────────────
  useEffect(() => {
    localStorage.removeItem("selectedOrganization");
    localStorage.removeItem("selectedTenantId");

    fetchOrgs();
    fetchRegions();
    fetchVerticals();

    api.get("/Auth/my-ip").then(res => {
      if (res.data?.detectedIp) setDetectedIp(res.data.detectedIp);
    }).catch(() => {});
  }, []);

  const fetchOrgs = async () => {
    try {
      const response = await api.get(`/Organizations`);
      setOrganizations(response.data);
    } catch (error) {} finally {
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await api.get("/Regions");
      setRegions(response.data);
    } catch (error) {}
  };

  const fetchVerticals = async () => {
    try {
      const response = await api.get("/Verticals");
      setVerticals(response.data);
    } catch (error) {}
  };

  // ── Polling ───────────────────────────────────────────────────────────────
  // Stash current orgs in a ref so the poll callback stays stable — otherwise
  // onPoll would re-create on every render and restart the interval.
  const orgsRef = useRef(organizations);
  useEffect(() => { orgsRef.current = organizations; }, [organizations]);

  const hasCreatingOrgs = useMemo(
    () => organizations.some((o) => o.status === 'Creating'),
    [organizations]
  );

  const onPoll = useCallback(async () => {
    try {
      const response = await api.get('/Organizations');
      const fresh = response.data;

      const prevById = new Map(orgsRef.current.map((o) => [o.organizationId, o]));
      for (const next of fresh) {
        const prev = prevById.get(next.organizationId);
        if (prev?.status === 'Creating' && next.status === 'Active') {
          setToast({ org: next });
          setRingOrgId(next.organizationId);
          // Drop the halo after ~6s. If another org finishes during that window
          // the latest one wins — fine for a "new" affordance.
          setTimeout(() => setRingOrgId((id) => (id === next.organizationId ? null : id)), 6000);
          // Keep the toast visible for 6s too (unless user dismisses).
          setTimeout(() => setToast((t) => (t?.org.organizationId === next.organizationId ? null : t)), 6000);
          refreshOrgHierarchy();
        }
      }

      setOrganizations(fresh);
    } catch (error) {}
  }, [refreshOrgHierarchy]);

  const { countdown } = usePollOrganizations({
    enabled: hasCreatingOrgs,
    intervalMs: 10000,
    onPoll,
  });

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSelect = (org) => {
    if (org.status && org.status !== 'Active') return;
    localStorage.setItem("selectedOrganization", JSON.stringify(org));
    const savedReturn = sessionStorage.getItem("returnUrl");
    sessionStorage.removeItem("returnUrl");
    const defaultDest = isAssignmentScopedOnly(org.organizationId) ? '/my-tasks' : '/dashboard';
    window.location.href = savedReturn || defaultDest;
  };

  const handleOrgCreated = (created) => {
    setOrganizations((prev) => {
      // If the org is already in state (e.g. the first poll beat us to it), replace
      // in place rather than appending — appending would produce a duplicate key.
      if (prev.some((o) => o.organizationId === created.organizationId)) {
        return prev.map((o) => (o.organizationId === created.organizationId ? created : o));
      }
      return [...prev, created];
    });
    refreshOrgHierarchy();
  };

  const handleRetry = async (org) => {
    setRetryingId(org.organizationId);
    try {
      const response = await api.post(`/Organizations/${org.organizationId}/retry-provisioning`);
      // Optimistically swap the failed row for the returned Creating row so
      // the tile flips states immediately; the poll will refresh afterwards.
      setOrganizations((prev) =>
        prev.map((o) => (o.organizationId === org.organizationId ? response.data : o))
      );
    } catch (error) {
      alert("Retry failed — please try again or contact support.");
    } finally {
      setRetryingId(null);
    }
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const hasTenantScopeRole = userRoles?.some(r => {
    const sv = typeof r.scope === 'number' ? r.scope : SCOPES[r.scope?.toUpperCase()];
    return sv !== undefined && sv <= SCOPES.TENANT;
  }) || false;

  const groupedOrgs = useMemo(() => {
    // Index by tenantId to guarantee exactly one group per unique tenant —
    // the previous sort/walk approach created a second group whenever the sort
    // interleaved orgs of the same tenantId, which triggered a React "duplicate
    // key" warning on the outer <div key={group.tenantId}>.
    const map = new Map();
    for (const org of organizations) {
      const key = org.tenantId ?? 'none';
      if (!map.has(key)) {
        map.set(key, { tenantId: key, tenantName: org.tenantName || 'No Tenant', orgs: [] });
      }
      map.get(key).orgs.push(org);
    }
    const groups = [...map.values()];
    groups.forEach((g) => g.orgs.sort((a, b) =>
      (a.organizationName || '').localeCompare(b.organizationName || '')
    ));
    groups.sort((a, b) => (a.tenantName || '').localeCompare(b.tenantName || ''));
    return groups;
  }, [organizations]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-theme-primary p-6 relative">
      {/* User Info & Sign Out */}
      <div className="absolute top-6 right-6 flex items-center gap-3">
        <div className="text-right">
          <p className="text-sm font-medium text-content-primary">{user?.userName}</p>
          <p className="text-xs text-content-secondary">{user?.email}</p>
        </div>
        <Button onClick={() => { window.location.href = '/logout'; }} variant="ghost" size="sm">
          <LogOut className="h-4 w-4 mr-1" />
          Sign Out
        </Button>
      </div>

      {/* ── IP Blocked Banner ──────────────────────────────────────────────── */}
      {ipBlockedMsg && (
        <div className="mb-6 mx-auto max-w-2xl bg-red-50 border border-red-300 rounded-xl p-4 flex items-start gap-3 shadow-sm">
          <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-red-100 border border-red-200 flex items-center justify-center">
            <Shield className="h-5 w-5 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-800 mb-0.5">Access Denied — IP Address Not Authorized</p>
            <p className="text-sm text-red-700 leading-snug">{ipBlockedMsg.message}</p>
            {ipBlockedMsg.detectedIp && (
              <p className="text-xs text-red-500 mt-1">
                Your detected IP: <span className="font-mono font-bold">{ipBlockedMsg.detectedIp}</span>
                {" "}— ask your administrator to add this IP to the organization's allowed list.
              </p>
            )}
          </div>
          <button
            onClick={() => setIpBlockedMsg(null)}
            className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Organization Cards */}
      <div>
        {/* Top Heading */}
        <div className="mb-10 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center">
              <Shield className="h-10 w-10 text-content-inverse" />
            </div>
          </div>
          <h1 className="text-4xl font-extrabold bg-gradient-to-r from-accent-cyan to-accent-purple bg-clip-text text-transparent tracking-tight">
            AXARIS™
          </h1>
          <p className="mt-2 text-content-secondary text-lg">
            Comprehensive organizational risk overview
          </p>
        </div>

        {/* Header row */}
        <div className="flex items-center justify-between px-6 mb-6">
          <div className="flex items-center gap-3">
            {canAddOrganization && (
              <Button onClick={() => setShowModal(true)} variant="primary" size="md">
                + New Organization
              </Button>
            )}
            {canAddOrganization && (
              <Button onClick={() => { window.location.href = '/tenant-users'; }} variant="secondary" size="md">
                <Users className="h-4 w-4 mr-2" />
                Manage Users
              </Button>
            )}
          </div>
          {hasCreatingOrgs && (
            <div className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md text-content-secondary bg-theme-primary border border-border">
              <RefreshCw className="w-3.5 h-3.5 animate-spin text-accent-cyan" />
              Auto-refresh · next in {countdown}s
            </div>
          )}
        </div>

        {/* Cards grouped by tenant */}
        <div className="px-6 space-y-8">
          {groupedOrgs.map((group, idx) => (
            <div key={group.tenantId}>
              {hasTenantScopeRole && idx > 0 && <hr className="border-border mb-8" />}
              {hasTenantScopeRole && (
                <h2 className="text-lg font-semibold text-content-secondary mb-4">
                  {group.tenantName}
                </h2>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {group.orgs.map((org) => {
                  if (org.status === 'Creating') {
                    return <CreatingTile key={org.organizationId} org={org} />;
                  }
                  if (org.status === 'Failed') {
                    return (
                      <FailedTile
                        key={org.organizationId}
                        org={org}
                        onRetry={handleRetry}
                        retrying={retryingId === org.organizationId}
                      />
                    );
                  }

                  // Active (or legacy rows with no status field) — existing layout
                  const blocked = isOrgIpBlocked(org, detectedIp);
                  const hasRing = ringOrgId === org.organizationId;
                  return (
                    <div
                      key={org.organizationId}
                      onClick={() => !blocked && handleSelect(org)}
                      title={blocked
                        ? `Access restricted from your IP (${detectedIp || "unknown"}). Contact your administrator to register this IP.`
                        : undefined}
                      style={hasRing ? {
                        boxShadow: '0 0 0 2px rgba(34,197,94,0.3), 0 0 24px rgba(34,197,94,0.2)',
                        transition: 'box-shadow 400ms ease-out',
                      } : undefined}
                      className={`relative bg-theme-card-solid border rounded-2xl p-5 transform transition duration-300
                        ${blocked
                          ? "border-red-300/60 opacity-60 cursor-not-allowed grayscale-[30%]"
                          : "border-border cursor-pointer hover:scale-105 hover:shadow-glow-cyan hover:border-accent-cyan/30"
                        }`}
                    >
                      {/* Lock badge — top-right corner */}
                      {blocked && (
                        <div className="absolute top-3 right-3 flex items-center gap-1 bg-red-100 border border-red-300 rounded-lg px-2 py-0.5">
                          <Lock className="w-3 h-3 text-red-600" />
                          <span className="text-xs font-semibold text-red-600">IP Restricted</span>
                        </div>
                      )}

                      <h3 className={`text-xl font-semibold mb-3 ${blocked ? "text-content-muted pr-28" : "text-accent-cyan pr-8"}`}>
                        {org.organizationName}
                      </h3>
                      <p className="flex items-center text-sm text-content-secondary mb-1">
                        <MapPin className="w-4 h-4 mr-2 text-content-muted" />
                        {org.organizationAddress1 || "N/A"}
                        {org.organizationAddress2 && `, ${org.organizationAddress2}`}
                      </p>
                      <p className="flex items-center text-sm text-content-secondary mb-1">
                        <Phone className="w-4 h-4 mr-2 text-content-muted" />
                        {org.organizationContactNo1 || "N/A"}
                        {org.organizationContactNo2 && `, ${org.organizationContactNo2}`}
                      </p>
                      <p className="flex items-center text-sm text-content-secondary mb-1">
                        <Factory className="w-4 h-4 mr-2 text-content-muted" />
                        {org.industry}
                      </p>
                      <p className="flex items-center text-sm text-content-secondary mb-1">
                        <Globe className="w-4 h-4 mr-2 text-content-muted" />
                        {org.regionName || "No Region"}
                      </p>

                      {/* Blocked footer */}
                      {blocked && (
                        <div className="mt-3 pt-3 border-t border-red-200/60 flex items-center gap-1.5">
                          <Lock className="w-3 h-3 text-red-400 flex-shrink-0" />
                          <p className="text-xs text-red-500 leading-snug">
                            Your IP <span className="font-mono font-semibold">{detectedIp}</span> is not authorized.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <NewOrganizationWizard
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        ownerTenants={ownerTenants}
        regions={regions}
        verticals={verticals}
        user={user}
        onCreated={handleOrgCreated}
      />

      {toast && (
        <SuccessToast
          toast={toast}
          onDismiss={() => setToast(null)}
          onOpen={(org) => { setToast(null); handleSelect(org); }}
        />
      )}
    </div>
  );
};

export default TenantSelection;
